import { produce as immerProduce } from 'immer';

// Re-export immer's types so any importer can used them by importing from `@utils`.
export * from 'immer';

/**
 * Enables or disables debug logging for cycle detection in Immer operations.
 * When set to `true`, additional debug information about cycles will be logged.
 * Useful for troubleshooting issues related to circular references in state updates.
 */
const DEBUG_IMMER_CYCLES = false;

// cycle-detector.ts
export type Path = (string | number)[]; // e.g. ['todo', 3, 'assignee']
export interface Cycle {
  /** Route from the root object to the place the cycle surfaced */
  path: Path;
  /** The actual object that was seen twice */
  node: unknown;
}

/**
 * Traverses an object graph starting from the given root and detects all cycles within it.
 *
 * A cycle is defined as a reference that causes a circular structure, i.e., an object
 * that is encountered again while traversing the current branch of the graph.
 *
 * The function supports plain objects, arrays, Maps, and Sets. For Maps and Sets,
 * the traversal includes their values (and for Maps, the key is included in the path).
 *
 * @param root - The root object or value to start traversal from.
 * @returns An array of `Cycle` objects, each containing the path to the cycle and the node where the cycle was detected.
 *
 * @remarks
 * - The function uses `WeakSet` to efficiently track visited nodes and avoid memory leaks.
 * - Only cycles reachable from the root are reported.
 * - The `Path` and `Cycle` types must be defined elsewhere in the codebase.
 */
export function findCycles(root: unknown): Cycle[] {
  const globalSeen = new WeakSet<object>(); // avoids re-walking subtrees
  const pathStack = new WeakSet<object>(); // tracks only the active branch
  const cycles: Cycle[] = [];

  function visit(value: unknown, path: Path): void {
    if (value === null || typeof value !== 'object') return;
    if (pathStack.has(value)) {
      // <-- real cycle
      cycles.push({ path, node: value });
      return;
    }
    if (globalSeen.has(value)) return; // subtree already checked

    pathStack.add(value);
    globalSeen.add(value);

    if (Array.isArray(value)) {
      value.forEach((v, i) => {
        visit(v, path.concat(i));
      });
    } else if (value instanceof Map) {
      value.forEach((v, k) => {
        visit(v, path.concat(`MapValue(${String(k)})`));
      });
    } else if (value instanceof Set) {
      let idx = 0;
      value.forEach(v => {
        visit(v, path.concat(`Set[${idx++}]`));
      });
    } else {
      for (const [k, v] of Object.entries(value)) {
        visit(v, path.concat(k));
      }
    }
    pathStack.delete(value); // pop on unwind
  }

  visit(root, []);
  return cycles;
}

/**
 * A wrapper around the `immer` `produce` function that conditionally uses a debug version
 * (`debugProduce`) when the `DEBUG_IMMER_CYCLES` flag is enabled. This allows for
 * enhanced debugging of cyclic references during state updates.
 *
 * @remarks
 * - When `DEBUG_IMMER_CYCLES` is `true`, this function delegates to `debugProduce` for
 *   additional cycle detection and debugging capabilities.
 * - Otherwise, it calls the standard `immerProduce` function for normal immutable state updates.
 *
 * @param args - The arguments to pass to the underlying `produce` function (either `immerProduce` or `debugProduce`).
 * @returns The result of the called `produce` function.
 *
 * @see {@link immerProduce}
 * @see {@link debugProduce}
 */
export const produce = ((...args: unknown[]) => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (DEBUG_IMMER_CYCLES) {
    // When debugging cycles, use the debug version
    return debugProduce(...(args as Parameters<typeof debugProduce>));
  }

  // Normal path - just call immer's produce directly
  return (immerProduce as (...args: unknown[]) => unknown)(...args);
}) as typeof immerProduce;

/**
 * A wrapper around `immerProduce` that adds cycle detection for debugging purposes.
 *
 * `debugProduce` can be used in two forms:
 * - Curried: `debugProduce(recipe)` returns a function `(state, ...args) => newState`
 * - Direct: `debugProduce(state, recipe, listener?)`
 *
 * After applying the recipe, it checks the resulting draft for cyclic references.
 * If cycles are detected, an error is thrown with the paths to the cycles.
 *
 * @param args - Either a recipe function (curried form) or state, recipe, and optional listener (direct form).
 * @returns A new state produced by applying the recipe, or a curried function for later use.
 * @throws {Error} If cycles are detected in the produced draft state.
 */
export const debugProduce = ((...args: unknown[]) => {
  if (args.length === 1) {
    // Curried form: debugProduce(recipe) => (state, ...args) => newState
    const recipe = args[0] as (...args: unknown[]) => unknown;
    return (...curryArgs: unknown[]) => {
      const [state, ...restArgs] = curryArgs;
      return immerProduce(state as object, (draft: object) => {
        const result = recipe(draft, ...restArgs);
        const cycles = findCycles(draft);
        if (cycles.length) {
          throw new Error(
            'Immer cycle detected at paths:\n' +
              // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
              cycles.map(c => c.path.join(' > ') + ` > ${c.node}`).join('\n')
          );
        }
        return result as object | undefined;
      });
    };
  } else {
    // Direct form: debugProduce(state, recipe, listener?)
    return debugProduceInternal(...args);
  }
}) as typeof immerProduce;

function debugProduceInternal(...args: unknown[]): unknown {
  const [state, recipeOrProducer, listenerOrUndefined] = args;

  return immerProduce(
    state as object,
    (draft: object) => {
      // // Check for cycles in the draft
      // const preCycles = findCycles(draft);
      // if (preCycles.length) {
      //   throw new Error(
      //     'Immer pre-cycles detected at paths:\n' +
      //     // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      //     preCycles.map(c => c.path.join(' > ') + ` > ${c.node}`).join('\n')
      //   );
      // }

      // Call the original recipe/producer
      const result = (recipeOrProducer as (...args: unknown[]) => unknown)(draft);

      // Check for cycles in the draft
      const cycles = findCycles(draft);
      if (cycles.length) {
        throw new Error(
          'Immer cycles detected at paths:\n' +
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            cycles.map(c => c.path.join(' > ') + ` > ${c.node}`).join('\n')
        );
      }

      return result as object | undefined;
    },
    listenerOrUndefined as Parameters<typeof immerProduce>[2]
  );
}

export function immerClone<T>(state: T): T {
  return immerProduce(state, _draft => {
    // No-op
  });
}
